import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Topbar from "../../components/Topbar/Topbar";
import Footer from "../../components/Footer/Footer";
import Hotelcard from "../../components/Hotelcard/Hotelcard";
import API from "../../API";
import { resolveImageUrl } from "../../constants";
import "./visitalgeria.css";
import {
  FaLandmark, FaWater, FaArchway, FaMosque, FaUmbrellaBeach, FaMountain,
  FaUniversity, FaUtensils, FaTheaterMasks, FaTree
} from "react-icons/fa";

// All 58 Algerian wilayas + common city names (EN/FR/AR/transliterations)
const ALGERIAN_LOCATIONS = [
  "algeria", "algerie", "algérie", "dz", "الجزائر",
  // 58 Wilayas (numeric order) — English, French, Arabic, common variants
  "adrar", "أدرار",
  "chlef", "الشلف",
  "laghouat", "laghwat", "الأغواط",
  "oum el bouaghi", "om el bouaghi", "أم البواقي",
  "batna", "باتنة",
  "bejaia", "béjaïa", "bejaïa", "bougie", "بجاية",
  "biskra", "بسكرة",
  "bechar", "béchar", "بشار",
  "blida", "البليدة",
  "bouira", "البويرة",
  "tamanrasset", "tamanghasset", "tamenghest", "تمنراست", "تمنغست",
  "tebessa", "tébessa", "تبسة",
  "tlemcen", "tilimsen", "تلمسان",
  "tiaret", "تيارت",
  "tizi ouzou", "tizi-ouzou", "تيزي وزو",
  "alger", "algiers", "algerie", "الجزائر العاصمة",
  "djelfa", "الجلفة",
  "jijel", "jijell", "جيجل",
  "setif", "sétif", "سطيف",
  "saida", "saïda", "سعيدة",
  "skikda", "سكيكدة",
  "sidi bel abbes", "sidi bel abbès", "سيدي بلعباس",
  "annaba", "bone", "bône", "عنابة",
  "guelma", "قالمة",
  "constantine", "qsentina", "قسنطينة",
  "medea", "médéa", "المدية",
  "mostaganem", "mostaghanem", "مستغانم",
  "msila", "m'sila", "المسيلة",
  "mascara", "mouaskar", "معسكر",
  "ouargla", "warqla", "ورقلة",
  "oran", "wahran", "وهران",
  "el bayadh", "elbayadh", "البيض",
  "illizi", "إليزي",
  "bordj bou arreridj", "bba", "برج بوعريريج",
  "boumerdes", "boumerdès", "بومرداس",
  "el tarf", "eltarf", "الطارف",
  "tindouf", "تندوف",
  "tissemsilt", "تيسمسيلت",
  "el oued", "eloued", "الوادي",
  "khenchela", "خنشلة",
  "souk ahras", "sukahras", "سوق أهراس",
  "tipaza", "tipasa", "تيبازة",
  "mila", "ميلة",
  "ain defla", "aïn defla", "عين الدفلى",
  "naama", "naâma", "النعامة",
  "ain temouchent", "aïn témouchent", "عين تموشنت",
  "ghardaia", "ghardaïa", "mozab", "m'zab", "غرداية",
  "relizane", "غليزان",
  "timimoun", "تيميمون",
  "bordj badji mokhtar", "برج باجي مختار",
  "ouled djellal", "أولاد جلال",
  "beni abbes", "béni abbès", "بني عباس",
  "in salah", "عين صالح",
  "in guezzam", "عين قزام",
  "touggourt", "تقرت",
  "djanet", "جانت",
  "el m'ghair", "el mghair", "المغير",
  "el menia", "المنيعة",
  // Tourist regions / landmarks
  "sahara", "kabylie", "aures", "aurès", "hoggar", "tassili", "djurdjura",
  "atlas", "tell", "mzab", "m'zab", "casbah", "قصبة", "الصحراء",
];

const HIGHLIGHTS = [
  { Icon: FaUniversity,     key: "highlight_history",   theme: "history"   },
  { Icon: FaUmbrellaBeach,  key: "highlight_beaches",   theme: "coast"     },
  { Icon: FaTree,           key: "highlight_sahara",    theme: "sahara"    },
  { Icon: FaUtensils,       key: "highlight_cuisine",   theme: "cuisine"   },
  { Icon: FaMountain,       key: "highlight_mountains", theme: "mountains" },
  { Icon: FaTheaterMasks,   key: "highlight_culture",   theme: "culture"   },
];

const FEATURED_CITIES = [
  {
    name: "Algiers", name_ar: "الجزائر العاصمة",
    Icon: FaLandmark,
    tagline: "Capital · Mediterranean coast",
    tagline_ar: "العاصمة · الساحل المتوسطي",
    gradient: "linear-gradient(135deg, #043263 0%, #1b7dd4 100%)",
    themes: ["history", "coast", "culture", "cuisine"],
    matchCity: "alger",
  },
  {
    name: "Oran", name_ar: "وهران",
    Icon: FaWater,
    tagline: "Port city · Spanish heritage",
    tagline_ar: "مدينة ميناء · تراث إسباني",
    gradient: "linear-gradient(135deg, #0a7ea4 0%, #30c5ff 100%)",
    themes: ["coast", "culture", "cuisine"],
    matchCity: "oran",
  },
  {
    name: "Constantine", name_ar: "قسنطينة",
    Icon: FaArchway,
    tagline: "City of bridges · Ancient medina",
    tagline_ar: "مدينة الجسور · مدينة عتيقة",
    gradient: "linear-gradient(135deg, #5a2a82 0%, #b84fb8 100%)",
    themes: ["history", "culture", "mountains"],
    matchCity: "constantine",
  },
  {
    name: "Ghardaia", name_ar: "غرداية",
    Icon: FaMosque,
    tagline: "M'Zab Valley · UNESCO heritage",
    tagline_ar: "وادي ميزاب · تراث اليونسكو",
    gradient: "linear-gradient(135deg, #b85c00 0%, #ff9533 100%)",
    themes: ["history", "sahara", "culture"],
    matchCity: "ghardaia",
  },
  {
    name: "Tipaza", name_ar: "تيبازة",
    Icon: FaUmbrellaBeach,
    tagline: "Roman ruins · Seaside beauty",
    tagline_ar: "آثار رومانية · جمال الساحل",
    gradient: "linear-gradient(135deg, #0a8f6f 0%, #3ad9a0 100%)",
    themes: ["history", "coast"],
    matchCity: "tipaza",
  },
  {
    name: "Tamanrasset", name_ar: "تمنراست",
    Icon: FaMountain,
    tagline: "Sahara desert · Hoggar mountains",
    tagline_ar: "الصحراء الكبرى · جبال الهقار",
    gradient: "linear-gradient(135deg, #8b2a0a 0%, #e8651a 100%)",
    themes: ["sahara", "mountains"],
    matchCity: "tamanrasset",
  },
];

export default function VisitAlgeria() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [hotels, setHotels] = useState([]);
  const [voyages, setVoyages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [themeIndex, setThemeIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [hotelsRes, voyagesRes] = await Promise.all([
          API.get("/properties/prop/search", {
            params: { location: "Algeria", page: 1, pageSize: 12 },
          }).catch(() => ({ data: { properties: [] } })),
          API.get("/voyage-organize/user/show", {
            params: { page: 1, limit: 12 },
          }).catch(() => ({ data: { voyages: [] } })),
        ]);

        setHotels(hotelsRes.data?.properties || []);

        const allVoyages = voyagesRes.data?.voyages || [];
        const filtered = allVoyages.filter((v) => {
          const text = `${v.cite || ""} ${v.location || ""} ${v.title || ""} ${v.description || ""}`.toLowerCase();
          return ALGERIAN_LOCATIONS.some((c) => text.includes(c));
        });
        setVoyages(filtered);
      } catch (err) {
        setError(err.message || "Error loading data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Count hotels per featured city (tolerant string match on `location` / `cite`)
  const countHotelsForCity = (cityName) => {
    if (!hotels.length) return 0;
    const needle = cityName.toLowerCase();
    return hotels.filter((h) => {
      const hay = `${h.location || ""} ${h.city || ""} ${h.cite || ""} ${h.name || ""}`.toLowerCase();
      return hay.includes(needle);
    }).length;
  };

  const activeTheme = HIGHLIGHTS[themeIndex]?.theme;
  const citiesForTheme = activeTheme
    ? FEATURED_CITIES.filter((c) => c.themes.includes(activeTheme))
    : FEATURED_CITIES;

  return (
    <div className={`VisitAlgeria ${isRTL ? "rtl" : ""}`}>
      <Topbar />
      <Navbar />

      {/* HERO */}
      <section className="va_hero">
        <div className="va_hero_overlay" />
        <div className="va_hero_content">
          <span className="va_hero_badge">🇩🇿 {t("visitalgeria.discover_algeria")}</span>
          <h1 className="va_hero_title">{t("visitalgeria.hero_title")}</h1>
          <p className="va_hero_subtitle">{t("visitalgeria.hero_subtitle")}</p>
          <div className="va_hero_actions">
            <a href="#hotels" className="va_btn va_btn_primary">{t("visitalgeria.explore_hotels")}</a>
            <a href="#voyages" className="va_btn va_btn_ghost">{t("visitalgeria.explore_voyages")}</a>
          </div>
        </div>
      </section>

      {/* WHY ALGERIA — tabbed explorer */}
      <section className="va_explorer">
        <div className="va_explorer_inner">
          <div className="va_explorer_head">
            <span className="va_split_eyebrow">{t("visitalgeria.why_algeria")}</span>
            <h2 className="va_split_title">{t("visitalgeria.why_algeria")}</h2>
            <p className="va_split_sub">{t("visitalgeria.top_destinations_sub")}</p>
          </div>

          {/* Theme tabs */}
          <div className="va_tabs" role="tablist" aria-label={t("visitalgeria.why_algeria")}>
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.Icon;
              const isActive = i === themeIndex;
              return (
                <button
                  key={h.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  className={`va_tab ${isActive ? "is-active" : ""}`}
                  onClick={() => setThemeIndex(i)}
                >
                  <span className="va_tab_icon"><Icon /></span>
                  <span className="va_tab_label">{t(`visitalgeria.${h.key}_title`)}</span>
                </button>
              );
            })}
          </div>

          {/* Active theme panel */}
          <div className="va_panel" role="tabpanel">
            <div className="va_panel_text">
              <h3 className="va_panel_title">
                {t(`visitalgeria.${HIGHLIGHTS[themeIndex].key}_title`)}
              </h3>
              <p className="va_panel_desc">
                {t(`visitalgeria.${HIGHLIGHTS[themeIndex].key}_desc`)}
              </p>
              <p className="va_panel_hint">
                {citiesForTheme.length}{" "}
                {citiesForTheme.length === 1 ? t("visitalgeria.destination") : t("visitalgeria.destinations")}
              </p>
            </div>

            <div className="va_panel_cities">
              {citiesForTheme.slice(0, 3).map((city) => {
                const Icon = city.Icon;
                const hotelCount = countHotelsForCity(city.matchCity || city.name);
                return (
                  <button
                    type="button"
                    key={city.name}
                    className="va_chip_card"
                    style={{ background: city.gradient }}
                    onClick={() => navigate(`/hotels?location=${encodeURIComponent(city.name)}`)}
                  >
                    <span className="va_chip_icon" aria-hidden="true"><Icon /></span>
                    <div className="va_chip_body">
                      <span className="va_chip_name">{isRTL ? city.name_ar : city.name}</span>
                      {hotelCount > 0 && (
                        <span className="va_chip_meta">
                          {hotelCount} {hotelCount === 1 ? t("visitalgeria.hotel") : t("visitalgeria.hotels_count")}
                        </span>
                      )}
                    </div>
                    <span className="va_chip_arrow" aria-hidden="true">{isRTL ? "←" : "→"}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR DESTINATIONS — static 3x2 grid */}
      <section className="va_destinations">
        <div className="va_destinations_inner">
          <div className="va_destinations_head">
            <div>
              <span className="va_split_eyebrow">{t("visitalgeria.top_destinations")}</span>
              <h2 className="va_split_title">{t("visitalgeria.top_destinations")}</h2>
            </div>
            <button className="va_see_all" onClick={() => navigate("/hotels")}>
              {t("visitalgeria.see_all")} {isRTL ? "←" : "→"}
            </button>
          </div>

          <div className="va_dest_grid">
            {FEATURED_CITIES.map((city) => {
              const Icon = city.Icon;
              const hotelCount = countHotelsForCity(city.matchCity || city.name);
              return (
                <button
                  type="button"
                  key={city.name}
                  className="va_dest_card"
                  style={{ background: city.gradient }}
                  onClick={() => navigate(`/hotels?location=${encodeURIComponent(city.name)}`)}
                >
                  <span className="va_dest_icon" aria-hidden="true"><Icon /></span>
                  <div className="va_dest_body">
                    <span className="va_dest_name">{isRTL ? city.name_ar : city.name}</span>
                    <span className="va_dest_tag">{isRTL ? city.tagline_ar : city.tagline}</span>
                    {hotelCount > 0 && (
                      <span className="va_dest_meta">
                        {hotelCount} {hotelCount === 1 ? t("visitalgeria.hotel") : t("visitalgeria.hotels_count")}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOTELS */}
      <section className="va_section" id="hotels">
        <div className="va_section_header">
          <h2 className="va_section_title">{t("visitalgeria.hotels_in_algeria")}</h2>
          <button className="va_see_all" onClick={() => navigate("/hotels")}>
            {t("visitalgeria.see_all")} →
          </button>
        </div>
        {loading ? (
          <p className="va_loading">{t("visitalgeria.loading")}</p>
        ) : hotels.length === 0 ? (
          <p className="va_empty">{t("visitalgeria.no_hotels")}</p>
        ) : (
          <div className="va_grid">
            {hotels.slice(0, 8).map((hotel) => (
              <Hotelcard key={hotel.id} hotel={hotel} />
            ))}
          </div>
        )}
      </section>

      {/* VOYAGES */}
      <section className="va_section" id="voyages">
        <div className="va_section_header">
          <h2 className="va_section_title">{t("visitalgeria.voyages_in_algeria")}</h2>
          <button className="va_see_all" onClick={() => navigate("/voyage")}>
            {t("visitalgeria.see_all")} →
          </button>
        </div>
        {loading ? (
          <p className="va_loading">{t("visitalgeria.loading")}</p>
        ) : voyages.length === 0 ? (
          <p className="va_empty">{t("visitalgeria.no_voyages")}</p>
        ) : (
          <div className="va_voyage_grid">
            {voyages.slice(0, 6).map((voyage) => {
              const img = voyage.images?.[0]?.url
                ? resolveImageUrl(voyage.images[0].url)
                : null;
              return (
                <div
                  key={voyage.id}
                  className="va_voyage_card"
                  onClick={() => navigate(`/voyage-org/${voyage.code}`)}
                >
                  {img && <img src={img} alt={voyage.title} />}
                  <div className="va_voyage_body">
                    <h3 className="va_voyage_title">{voyage.title}</h3>
                    <p className="va_voyage_city">📍 {voyage.cite || voyage.location}</p>
                    {voyage.description && (
                      <p className="va_voyage_desc">{voyage.description.slice(0, 120)}...</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="va_cta">
        <div className="va_cta_content">
          <h2>{t("visitalgeria.cta_title")}</h2>
          <p>{t("visitalgeria.cta_subtitle")}</p>
          <button className="va_btn va_btn_primary" onClick={() => navigate("/visa")}>
            {t("visitalgeria.apply_visa")}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
